import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Input,
  SearchableSelect,
  useToast,
  type SearchableSelectOption,
} from '@kss/ui';
import { ArrowLeft, Save } from 'lucide-react';
import {
  orderReturnService,
  salesOrderService,
  type SalesOrder,
} from '@kss/api';
import { loadSalesOrderOptions } from '../shared/formLoaders';
import { decodeId } from '../../utils/idCodec';

/**
 * Order-return form. Splits out from `CrudForm` because the standard
 * generic form has no hook for cross-field side effects, and this
 * page needs three:
 *
 *   1. When the user picks a Sales Order, fetch that order and
 *      auto-fill `totalAmount` from `salesOrder.totalAmount`.
 *   2. The total-amount input is then **read-only** — totals come
 *      from the source order, never user-entered.
 *   3. Refund amount is clamped to `totalAmount` — the form blocks
 *      Save if `refundAmount > totalAmount`.
 *
 * Bug ref: AI Agent (round 2) — Sales → Order Returns spec.
 */
type F = {
  returnNumber: string;
  returnDate: string;
  salesOrderId: string;
  storeId: string;
  reason: string;
  totalAmount: number;
  refundAmount: number;
  status: string;
  notes: string;
  isActive: boolean;
};

const today = new Date().toISOString().slice(0, 10);
const STATUS_OPTIONS = ['Requested', 'Approved', 'Rejected', 'Refunded'];

const empty: F = {
  returnNumber: '',
  returnDate: today,
  salesOrderId: '',
  storeId: '',
  reason: '',
  totalAmount: 0,
  refundAmount: 0,
  status: 'Requested',
  notes: '',
  isActive: true,
};

export default function NewOrderReturn() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const { id: encodedId } = useParams();
  const id = encodedId ? decodeId(encodedId) : undefined;
  const isEdit = !!id;

  const [form, setForm] = useState<F>(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof F>(k: K, v: F[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // Edit mode — hydrate from server.
  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    setLoading(true);
    orderReturnService
      .get(id)
      .then((r) => {
        if (cancelled) return;
        setForm({
          returnNumber: r.returnNumber ?? '',
          returnDate: r.returnDate?.slice(0, 10) ?? today,
          // Live API stores in `orderId` (with `orderType=1` for sales);
          // legacy rows still carry `salesOrderId`. Support both.
          salesOrderId: r.orderId ?? r.salesOrderId ?? '',
          storeId: r.storeId ?? '',
          reason: r.reason ?? '',
          totalAmount: Number(r.totalAmount ?? 0),
          refundAmount: Number(r.refundAmount ?? 0),
          status: r.status ?? 'Requested',
          notes: r.notes ?? '',
          isActive: !!r.isActive,
        });
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Failed to load';
        setError(msg);
        toastRef.current.error('Load failed', msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  /**
   * Auto-fill the total amount from the picked sales order. We only
   * overwrite `totalAmount` when the user just changed `salesOrderId`
   * — otherwise loading the same SO in edit mode would clobber a
   * manually-edited refund/total pair from history.
   */
  const lastSalesOrderId = useRef<string>('');
  useEffect(() => {
    const so = form.salesOrderId;
    if (!so || so === lastSalesOrderId.current) return;
    lastSalesOrderId.current = so;
    let cancelled = false;
    salesOrderService
      .get(so)
      .then((order: SalesOrder) => {
        if (cancelled) return;
        const total = Number(order.totalAmount ?? 0);
        setForm((p) => ({
          ...p,
          totalAmount: total,
          // Clamp the existing refund to the new ceiling.
          refundAmount: Math.min(p.refundAmount, total),
          // Auto-fill storeId from the source sales order so the
          // backend "store is invalid" check passes — the return is
          // always issued against the same store that fulfilled the
          // sale. Bug ref: AI Agent (round 3) — "store is invalid".
          storeId: order.storeId ?? p.storeId,
        }));
      })
      .catch(() => {
        /* silent — leave totals at 0; the validate guard will trip
           if user tries to save without a usable amount. */
      });
    return () => {
      cancelled = true;
    };
  }, [form.salesOrderId]);

  const refundExceedsTotal = form.refundAmount > form.totalAmount;

  const valid =
    !!form.returnNumber.trim() &&
    !!form.salesOrderId.trim() &&
    !refundExceedsTotal;

  const handleSave = async () => {
    if (!valid) {
      toast.error(
        'Fix errors',
        refundExceedsTotal
          ? 'Refund amount cannot exceed total amount.'
          : 'Return number and sales order are required.',
      );
      return;
    }
    setSaving(true);
    try {
      const body = {
        orderReturnId: id,
        returnNumber: form.returnNumber,
        returnDate: form.returnDate || undefined,
        /* Live API uses `orderId` + `orderType` discriminator (1 =
           SalesOrder). Send both. `salesOrderId` kept on the wire
           for backward compat with older controllers — the new
           controller ignores it. */
        orderId: form.salesOrderId || undefined,
        orderType: 1,
        salesOrderId: form.salesOrderId || undefined,
        storeId: form.storeId || undefined,
        reason: form.reason || undefined,
        totalAmount: form.totalAmount || undefined,
        refundAmount: form.refundAmount || undefined,
        status: form.status || undefined,
        notes: form.notes || undefined,
        isActive: form.isActive,
      };
      if (isEdit) {
        await orderReturnService.update([body]);
      } else {
        await orderReturnService.create([body]);
      }
      toast.success('Saved', 'Order return saved');
      navigate('/panel/order-returns');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  // Memo so the SearchableSelect's eager loader doesn't fire repeatedly.
  const salesOrderLoader = useMemo(
    () => async (): Promise<SearchableSelectOption[]> => loadSalesOrderOptions(),
    [],
  );

  return (
    <div className="-m-6 p-[3px]">
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      <Card className="p-4">
        <header className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/panel/order-returns')}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <h1 className="text-base font-semibold text-[var(--color-text-primary)]">
              {isEdit ? 'Edit order return' : 'New order return'}
            </h1>
          </div>
        </header>

        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label="Return number *"
            placeholder="RET-1001"
            value={form.returnNumber}
            onChange={(e) => set('returnNumber', e.target.value)}
          />
          <Input
            label="Return date"
            type="date"
            value={form.returnDate}
            onChange={(e) => set('returnDate', e.target.value)}
          />
          <SearchableSelect
            label="Sales order *"
            value={form.salesOrderId}
            onChange={(v) => set('salesOrderId', v)}
            loadOptions={salesOrderLoader}
            placeholder="Pick a sales order"
            searchPlaceholder="Search sales orders..."
          />
          {/* Store — auto-filled from the selected sales order. Shown
              read-only so users see what will post; backend rejects
              with "store is invalid" if missing. Bug ref: AI Agent
              (round 3) — Order Returns "store is invalid". */}
          <Input
            label="Store (auto)"
            value={form.storeId}
            readOnly
            disabled
            hint="Inherited from the picked sales order"
          />
          <Input
            label="Reason"
            placeholder="Damaged on delivery"
            value={form.reason}
            onChange={(e) => set('reason', e.target.value)}
          />
          {/* Total amount — READ-ONLY. Sourced from the picked sales
              order so user can't fabricate a refund larger than the
              sale itself. Field is greyed out so the constraint is
              obvious; the value still posts on save. */}
          <Input
            label="Total amount (₹)"
            type="number"
            value={String(form.totalAmount)}
            readOnly
            disabled
            hint="Auto-filled from the selected sales order"
          />
          <Input
            label="Refund amount (₹)"
            type="number"
            value={String(form.refundAmount)}
            onChange={(e) => {
              const next = Math.max(0, Number(e.target.value || 0));
              // Clamp at total so the user physically can't enter more.
              set('refundAmount', Math.min(next, form.totalAmount));
            }}
            error={
              refundExceedsTotal
                ? `Refund cannot exceed ₹${form.totalAmount.toLocaleString('en-IN')}`
                : undefined
            }
          />
          <div>
            <label className="text-xs font-medium text-[var(--color-text-secondary)]">
              Status
            </label>
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)]"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--color-text-secondary)]">
              Active
            </label>
            <select
              value={form.isActive ? 'active' : 'inactive'}
              onChange={(e) => set('isActive', e.target.value === 'active')}
              className="mt-1 h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)]"
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[var(--color-text-secondary)]">
              Notes
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
            />
          </div>
        </div>

        <footer className="mt-4 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/panel/order-returns')}
            disabled={saving || loading}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!valid}>
            <Save className="h-4 w-4" /> {isEdit ? 'Update' : 'Save'}
          </Button>
        </footer>
      </Card>
    </div>
  );
}
